import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatestRef } from '@federa/components/hooks/useLatestRef';
import { isBrowser } from '@federa/utils/browser/detection';
import { API_TIMEOUTS } from '@federa/const/timing';
import { DEFAULT_GUEST_ROLE, PLACEHOLDER_USERNAME } from '@federa/const/auth-session';
import { useAppDispatch, useAuthState, useMessagingState, useUserState } from './state.tsx';
import { createMessagingClient } from './messaging-client.ts';
import { resolveChatRoomWsBase } from './transport.ts';
import { createClientHandlers, createOptimisticMessage, dispatchOptimistic } from './useMessagingWebsocket.helpers.ts';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type { AppDispatch as DispatchFn } from '@federa/types/react-app-state';
import type { ChatFileAttachment as FileAttachment, MessagingRefs, MessagingClientHandle } from '@federa/types/ui-chat-messaging';

const OPTIMISTIC_TIMEOUT_MS = API_TIMEOUTS.longRunning;

interface MessagingWebsocketOptions {
	room: ChatRoomStruct | null;
	reconnectTrigger?: number;
	/** Gate the connection on pane visibility; when false the WS never opens. Defaults true. */
	enabled?: boolean;
}

function useMessagingRefs(messaging: ReturnType<typeof useMessagingState>, room: ChatRoomStruct | null): MessagingRefs {
	const messagesRef = useLatestRef(messaging.currentMessages);
	const roomsRef = useLatestRef(messaging.chatRoomList);
	const participantsRef = useLatestRef(messaging.currentParticipants);
	const currentRoomRef = useLatestRef(room);
	const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

	return useMemo(
		() => ({
			messages: messagesRef,
			rooms: roomsRef,
			participants: participantsRef,
			currentRoom: currentRoomRef,
			timeoutIds: timeoutIdsRef,
		}),
		[messagesRef, roomsRef, participantsRef, currentRoomRef],
	);
}

interface MessagingConnectionConfig {
	ready: boolean;
	userId: string;
	username: string | undefined;
	role: string | undefined;
	roomId: string;
	conversationType: string | undefined;
	refs: MessagingRefs;
	dispatch: DispatchFn;
	clientRef: React.MutableRefObject<MessagingClientHandle | null>;
	reconnectTrigger: number;
	/** The socket's open/closed edge, delivered by the client the moment it happens. */
	onConnectionChange: (connected: boolean) => void;
}

/** Cancel every pending message timeout and empty the ref -- the teardown BOTH exits of the connection effect owe.
 *  ONE spelling because the two exits are the same decision reached two ways (the effect refusing to connect, and the
 *  cleanup after one that did): a drain that cleared the timers on one path and left the array populated on the other
 *  would leak a growing id list across reconnects, which is invisible until a stale timer fires into a dead client. */
function drainPendingTimeouts(timeoutIdsRef: MessagingRefs['timeoutIds']): void {
	for (const timeoutId of timeoutIdsRef.current) {
		clearTimeout(timeoutId);
	}
	timeoutIdsRef.current = [];
}

function useMessagingConnection(config: MessagingConnectionConfig) {
	const { ready, userId, username, role, roomId, conversationType, refs, dispatch, clientRef, reconnectTrigger, onConnectionChange } = config;

	useEffect(() => {
		const timeoutIdsRef = refs.timeoutIds;
		clientRef.current?.disconnect();
		clientRef.current = null;
		onConnectionChange(false);
		if (!isBrowser() || !ready) {
			drainPendingTimeouts(timeoutIdsRef);
			return () => undefined;
		}
		const handlers = createClientHandlers(refs, dispatch);
		const handle = createMessagingClient({
			userId,
			username: username || PLACEHOLDER_USERNAME,
			role: role || DEFAULT_GUEST_ROLE,
			roomId,
			conversationType,
			wsUrlBase: resolveChatRoomWsBase(),
			...handlers,
			onConnectionChange,
		});
		clientRef.current = handle;
		return () => {
			handle.disconnect();
			onConnectionChange(false);
			drainPendingTimeouts(timeoutIdsRef);
		};
	}, [conversationType, dispatch, ready, reconnectTrigger, roomId, role, userId, username, refs, clientRef, onConnectionChange]);
}

interface SendMessageCallbackOptions {
	clientRef: React.MutableRefObject<MessagingClientHandle | null>;
	roomId: string;
	userId: string;
	username: string | undefined;
	refs: MessagingRefs;
	dispatch: DispatchFn;
}

function useSendMessageCallback(options: SendMessageCallbackOptions) {
	const { clientRef, roomId, userId, username, refs, dispatch } = options;
	return useCallback(
		(content: string, files?: FileAttachment[]) => {
			if (!clientRef.current) return false;
			dispatchOptimistic(
				createOptimisticMessage(content, {
					roomId,
					userId,
					username: username || 'You',
					files,
				}),
				{ refs, dispatch, timeoutMs: OPTIMISTIC_TIMEOUT_MS },
			);
			return clientRef.current.sendMessage(content.trim(), files);
		},
		[dispatch, roomId, userId, username, clientRef, refs],
	);
}

export function useMessagingWebsocket({ room, reconnectTrigger = 0, enabled = true }: MessagingWebsocketOptions) {
	const dispatch = useAppDispatch();
	const auth = useAuthState();
	const user = useUserState();
	const messaging = useMessagingState();
	const clientRef = useRef<MessagingClientHandle | null>(null);
	const refs = useMessagingRefs(messaging, room);

	const roomId = room?.id ?? '';
	const conversationType = room?.conversationType;
	const ready = enabled && auth.isAuthInitialized && Boolean(roomId && user.userId);
	const { userId, username, role } = user;
	// EVENT-DRIVEN, not polled. The provider used to sample `isConnected()` every 500ms, so the composer stayed on
	// "Connecting..." up to half a second after the socket had opened; the client already reports the edge.
	const [connected, setConnected] = useState(false);

	useMessagingConnection({
		ready,
		userId,
		username,
		role,
		roomId,
		conversationType,
		refs,
		dispatch,
		clientRef,
		reconnectTrigger,
		onConnectionChange: setConnected,
	});

	const sendMessage = useSendMessageCallback({
		clientRef,
		roomId,
		userId,
		username,
		refs,
		dispatch,
	});
	const isConnected = useCallback(() => connected, [connected]);
	return { sendMessage, isConnected };
}
